import { useState, useEffect } from 'react';

interface LetterState {
  char: string;
  isSettled: boolean;
  currentFontIndex: number;
  finalFont: string;
}

interface AnimationConfig {
  staticWord: string;
  cyclingWords: string[];
  typingSpeed: number;
  backspaceSpeed: number;
  pauseAfterComplete: number;
  pauseAfterBackspace: number;
  settlingDelay: number;
  fontCycleSpeed: number;
  staticWordFont: string;
  cyclingWordFont: string;
  dynamicSizing: boolean;
  baselineFontSize: string;
}

const defaultConfig: AnimationConfig = {
  staticWord: 'embrace',
  cyclingWords: ['CHANGE', 'AI', 'TRANSFORMATION', 'DATA', 'INTELLIGENCE', 'THE CLOUD'],
  typingSpeed: 133,
  backspaceSpeed: 80,
  pauseAfterComplete: 3500,
  pauseAfterBackspace: 500,
  settlingDelay: 300,
  fontCycleSpeed: 120,
  staticWordFont: 'font-serif italic',
  cyclingWordFont: 'font-sans font-black',
  dynamicSizing: true,
  baselineFontSize: 'text-5xl sm:text-7xl lg:text-9xl'
};

const AnimatedTagline = ({ config = defaultConfig }: { config?: Partial<AnimationConfig> }) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  const [phase, setPhase] = useState<'typing' | 'complete' | 'backspacing' | 'cycling'>('typing');
  const [currentCyclingWordIndex, setCurrentCyclingWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [letters, setLetters] = useState<LetterState[]>([]);
  const [isInitialComplete, setIsInitialComplete] = useState(false);

  const fonts = [
    'font-titan font-normal',
    'font-space font-bold italic',
    'font-geologica font-thin',
    'font-jetbrains font-light',
    'font-serif font-black italic',
    'font-zen font-black',
    'font-space font-normal',
    'font-geologica font-black',
    'font-jetbrains font-bold italic',
    'font-sans font-extrabold',
    'font-titan font-normal tracking-wide',
    'font-zen font-light',
    'font-mono font-medium',
    'font-geologica font-medium',
    'font-serif font-light italic',
  ];

  const getCurrentWords = () => {
    if (phase === 'cycling' && isInitialComplete) {
      return [finalConfig.staticWord, finalConfig.cyclingWords[currentCyclingWordIndex]];
    }
    return [finalConfig.staticWord, finalConfig.cyclingWords[0]];
  };

  // Calculate dynamic font size for each word
  const getDynamicFontSize = (word: string): string => {
    if (!finalConfig.dynamicSizing) {
      return finalConfig.baselineFontSize;
    }

    // Calculate relative sizes based on character count
    // Use the shortest cycling word as baseline (DATA = 4 chars)
    const shortestLength = Math.min(...finalConfig.cyclingWords.map(w => w.length));
    const currentLength = word.length;
    
    // Scale factor: longer words get smaller fonts
    const scaleFactor = shortestLength / currentLength;
    
    // Map scale factors to Tailwind font size classes with mobile responsiveness
    if (scaleFactor >= 1.0) {
      // Short words: DATA (4), CHANGE (6)
      return 'text-5xl sm:text-7xl lg:text-9xl';
    } else if (scaleFactor >= 0.6) {
      // Medium words: INTELLIGENCE (12)  
      return 'text-4xl sm:text-5xl lg:text-7xl';
    } else {
      // Long words: TRANSFORMATION (14), THE CLOUD (9 with space)
      return 'text-3xl sm:text-4xl lg:text-6xl';
    }
  };

  // Final fonts for each word
  const getWordFinalFonts = () => [
    finalConfig.staticWordFont,
    finalConfig.cyclingWordFont
  ];

  // Get final font for a specific character
  const getFinalFont = (globalIndex: number) => {
    const words = getCurrentWords();
    const wordFinalFonts = getWordFinalFonts();
    let charCount = 0;
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      if (globalIndex < charCount + words[wordIndex].length) {
        return wordFinalFonts[wordIndex];
      }
      charCount += words[wordIndex].length + 1; // +1 for space
    }
    return wordFinalFonts[0];
  };

  // Initialize or add a new letter
  const addLetter = (char: string, globalIndex: number) => {
    const finalFont = getFinalFont(globalIndex);
    const newLetter: LetterState = {
      char,
      isSettled: false,
      currentFontIndex: 0,
      finalFont
    };
    
    setLetters(prev => [...prev, newLetter]);
  };

  // Main animation control
  useEffect(() => {
    
    if (phase === 'typing') {
      const timer = setTimeout(() => {
        if (currentCharIndex < finalConfig.staticWord.length) {
          // Typing static word
          const globalIndex = letters.length;
          addLetter(finalConfig.staticWord[currentCharIndex], globalIndex);
          setCurrentCharIndex(prev => prev + 1);
          
          setTimeout(() => {
            setLetters(prev => prev.map((letter, index) => 
              index === globalIndex ? { ...letter, isSettled: true } : letter
            ));
          }, finalConfig.settlingDelay);
        } else if (currentCharIndex === finalConfig.staticWord.length) {
          // Add space
          const globalIndex = letters.length;
          addLetter(' ', globalIndex);
          setCurrentCharIndex(prev => prev + 1);
          
          setTimeout(() => {
            setLetters(prev => prev.map((letter, index) => 
              index === globalIndex ? { ...letter, isSettled: true } : letter
            ));
          }, 50);
        } else {
          // Typing cycling word
          const cyclingWordIndex = currentCharIndex - finalConfig.staticWord.length - 1;
          const currentCyclingWord = finalConfig.cyclingWords[currentCyclingWordIndex];
          
          if (cyclingWordIndex < currentCyclingWord.length) {
            const globalIndex = letters.length;
            addLetter(currentCyclingWord[cyclingWordIndex], globalIndex);
            setCurrentCharIndex(prev => prev + 1);
            
            setTimeout(() => {
              setLetters(prev => prev.map((letter, index) => 
                index === globalIndex ? { ...letter, isSettled: true } : letter
              ));
            }, finalConfig.settlingDelay);
          } else {
            // Complete typing, pause before cycling
            setPhase('complete');
            setIsInitialComplete(true);
            setTimeout(() => {
              setPhase('backspacing');
            }, finalConfig.pauseAfterComplete);
          }
        }
      }, finalConfig.typingSpeed);

      return () => clearTimeout(timer);
    }
    
    if (phase === 'backspacing') {
      const timer = setTimeout(() => {
        const staticWordLength = finalConfig.staticWord.length + 1; // +1 for space
        
        if (letters.length > staticWordLength) {
          // Remove last character
          setLetters(prev => prev.slice(0, -1));
        } else {
          // Done backspacing, pause then start typing next word
          const nextIndex = (currentCyclingWordIndex + 1) % finalConfig.cyclingWords.length;
          setCurrentCyclingWordIndex(nextIndex);
          setCurrentCharIndex(staticWordLength);
          setTimeout(() => {
            setPhase('cycling');
          }, finalConfig.pauseAfterBackspace);
        }
      }, finalConfig.backspaceSpeed);

      return () => clearTimeout(timer);
    }
    
    if (phase === 'cycling') {
      const timer = setTimeout(() => {
        const currentCyclingWord = finalConfig.cyclingWords[currentCyclingWordIndex];
        const cyclingWordIndex = currentCharIndex - finalConfig.staticWord.length - 1;
        
        if (cyclingWordIndex < currentCyclingWord.length) {
          const globalIndex = letters.length;
          addLetter(currentCyclingWord[cyclingWordIndex], globalIndex);
          setCurrentCharIndex(prev => prev + 1);
          
          setTimeout(() => {
            setLetters(prev => prev.map((letter, index) => 
              index === globalIndex ? { ...letter, isSettled: true } : letter
            ));
          }, finalConfig.settlingDelay);
        } else {
          // Complete cycling word, pause before backspacing
          setTimeout(() => {
            setPhase('backspacing');
          }, finalConfig.pauseAfterComplete);
        }
      }, finalConfig.typingSpeed);

      return () => clearTimeout(timer);
    }
  }, [phase, currentCharIndex, currentCyclingWordIndex, letters.length]);

  // Font cycling for unsettled letters
  useEffect(() => {
    if (phase === 'complete') return;

    const interval = setInterval(() => {
      setLetters(prev => prev.map(letter => 
        !letter.isSettled 
          ? { ...letter, currentFontIndex: (letter.currentFontIndex + 1) % fonts.length }
          : letter
      ));
    }, finalConfig.fontCycleSpeed);

    return () => clearInterval(interval);
  }, [phase]);

  const renderLetter = (letter: LetterState, index: number) => {
    const currentFont = letter.isSettled 
      ? letter.finalFont 
      : fonts[letter.currentFontIndex];
    
    // Determine if this letter is part of the static word or cycling word
    const staticWordLength = finalConfig.staticWord.length;
    const isStaticWord = index < staticWordLength;
    const isSpace = index === staticWordLength;
    
    // Get font size class based on whether it's the static word or cycling word
    let fontSizeClass = finalConfig.baselineFontSize; // Default for static word
    if (!isStaticWord && !isSpace) {
      // This is part of the cycling word, use dynamic sizing
      const currentCyclingWord = finalConfig.cyclingWords[currentCyclingWordIndex];
      fontSizeClass = getDynamicFontSize(currentCyclingWord);
    }

    if (letter.char === ' ') {
      // Only use line break for the space between "embrace" and cycling word
      if (index === staticWordLength) {
        return <br key={index} />;
      }
      // For spaces within cycling words, use regular space
      return <span key={index} className="inline-block w-2 lg:w-4" />;
    }

    return (
      <span
        key={index}
        className="inline-block relative"
      >
        {/* Hidden spacer with final font to reserve correct width */}
        <span
          className={`${letter.finalFont} ${fontSizeClass} text-glam-primary dark:text-white invisible`}
          aria-hidden="true"
        >
          {letter.char}
        </span>
        {/* Visible letter with current font */}
        <span
          className={`${currentFont} ${fontSizeClass} text-glam-primary dark:text-white absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
            letter.isSettled ? 'opacity-100' : 'opacity-80'
          }`}
        >
          {letter.char}
        </span>
      </span>
    );
  };

  // Never show final settled state since we're always cycling

  // Calculate cursor font size based on what's being typed
  const getCursorFontSize = () => {
    const staticWordLength = finalConfig.staticWord.length;
    if (letters.length <= staticWordLength) {
      // Typing "embrace" 
      return finalConfig.baselineFontSize;
    } else {
      // Typing cycling word
      const currentCyclingWord = finalConfig.cyclingWords[currentCyclingWordIndex];
      return getDynamicFontSize(currentCyclingWord);
    }
  };

  const cursorFontSize = getCursorFontSize();

  // Split letters into lines based on the line break
  const staticWordLength = finalConfig.staticWord.length;
  const firstLineLetters = letters.slice(0, staticWordLength);
  const lineBreakIndex = staticWordLength;
  const secondLineLetters = letters.slice(lineBreakIndex + 1); // Skip the line break character
  
  return (
    <div className="font-black leading-[0.85] tracking-tight mb-12" style={{ minHeight: '200px' }}>
      {/* First line: "embrace" */}
      <div className="flex items-baseline">
        {firstLineLetters.map((letter, index) => renderLetter(letter, index))}
        {/* Show cursor on first line only when typing "embrace" */}
        {phase === 'typing' && letters.length <= staticWordLength && (
          <span className={`${cursorFontSize} animate-pulse text-glam-primary dark:text-white`} style={{ width: '0.15em' }}>|</span>
        )}
      </div>
      
      {/* Second line: cycling words */}
      {letters.length > staticWordLength && (
        <div className="flex items-baseline">
          {secondLineLetters.map((letter, index) => renderLetter(letter, staticWordLength + 1 + index))}
          
          {/* Zero-width placeholder to keep cursor on second line during backspacing */}
          {(phase === 'backspacing') && secondLineLetters.length === 0 && (
            <span className="inline-block" style={{ width: '0px', height: '1em' }} />
          )}
          
          {/* Typing cursor for cycling words */}
          {(phase === 'typing' || phase === 'cycling') && letters.length > staticWordLength && (
            <span className={`${cursorFontSize} animate-pulse text-glam-primary dark:text-white`} style={{ width: '0.15em' }}>|</span>
          )}
        </div>
      )}
    </div>
  );
};

export default AnimatedTagline;